import { CheckIcon } from "lucide-react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { OptionalLabel } from "@/components/OptionalLabel";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";

export const FormButtonGroup = <T extends Record<string, unknown>>({
  description,
  disabled,
  form,
  fieldName,
  label,
  onSelect,
  optional,
  options,
  reduceOpacityValues,
  valueToSelectedClassName,
  valueToIconMap,
}: {
  description?: string;
  disabled?: boolean;
  form: UseFormReturn<T>;
  fieldName: FieldPath<T>;
  label: string;
  onSelect?: (value: string) => void;
  optional?: boolean;
  options: { value: string; label: string }[];
  valueToSelectedClassName?: Record<string, string>;
  reduceOpacityValues?: string[];
  valueToIconMap?: Record<string, React.ElementType>;
}) => {
  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem className="min-w-0">
          <Row className="items-baseline gap-2">
            <FormLabel className="text-slate-700">
              {label}
              {optional && <OptionalLabel className="ml-1.5" />}
            </FormLabel>
            {description && (
              <Text variant="muted" className="text-xs">
                {description}
              </Text>
            )}
          </Row>
          <Row className="gap-3 overflow-x-auto">
            {options.map((option) => {
              const ButtonIcon = valueToIconMap?.[option.value];
              const isSelected = field.value === option.value;
              return (
                <Button
                  disabled={disabled}
                  key={option.value}
                  type="button"
                  variant="outlineSecondary"
                  className={cn(
                    "shrink-0",
                    isSelected &&
                      (valueToSelectedClassName?.[option.value] ??
                        "border-primary-400 bg-primary-50 text-primary-900 hover:bg-primary-100 hover:text-primary-900"),
                  )}
                  onClick={() => {
                    mixpanelUtils.trackButtonClick(
                      "Form button group option",
                      getScreenFromPathname(location.pathname),
                      { value: option.value },
                    );
                    field.onChange(option.value);
                    onSelect?.(option.value);
                  }}
                >
                  {isSelected && (
                    <CheckIcon className="h-3.5 w-3.5 text-primary-700" />
                  )}
                  {ButtonIcon && (
                    <ButtonIcon
                      className={cn(
                        reduceOpacityValues?.includes(option.value) &&
                          "opacity-10",
                      )}
                    />
                  )}
                  {option.label}
                </Button>
              );
            })}
          </Row>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
